import { forwardRef, useState } from 'react';

import { RiCloseFill } from 'react-icons/ri';

import {
  Popover,
  Button,
  NumberInput,
  Group,
  Stack,
  useDisclosure,
  ActionIcon,
  MantineSize,
  showNotification,
  useClickOutside
} from '@ui';

import classes from './NumberRangeInput.module.css';

export interface NumberRangeInputProps {
  value: [number | undefined, number | undefined];
  onChange: (value: [number | undefined, number | undefined]) => void;
  startLabel: string;
  endLabel?: string;
  size?: MantineSize;
  toRequired?: boolean;
}

const NumberRangeInput = forwardRef<HTMLInputElement, NumberRangeInputProps>(
  (props, ref) => {
    const { value, startLabel, endLabel, onChange, toRequired, size = 'md' } = props;
    const [from, to] = value;
    const [inputValues, setInputValues] = useState<
      [number | undefined, number | undefined]
    >([from, to]);
    const [opened, { close, open }] = useDisclosure(false);
    const outsideRef = useClickOutside(close);

    const handleChange = (clear?: boolean) => {
      if (toRequired && (!inputValues[0] || !inputValues[1])) {
        showNotification({
          title: 'Error',
          message: 'Please fill both values',
          color: 'danger'
        });
        return;
      }
      if (clear) {
        setInputValues([undefined, undefined]);
        onChange([undefined, undefined]);
      } else {
        onChange(inputValues);
      }
      close();
    };

    return (
      <Popover width={220} position="bottom" opened={opened}>
        <Popover.Target>
          <Button
            size={size}
            color="secondary"
            variant="outline"
            onClick={() => (opened ? close() : open())}
            className={classes.targetButtonLabel}
            mod={{
              'button-status': to || from ? 'active' : 'default'
            }}
          >
            {startLabel}
            {from && !to ? `: From ${from}` : ''}
            {!from && to ? `: To ${to}` : ''}
            {from && to ? `: ${from} - ${to}` : ''}
            {from || to ? ` ${endLabel}` : ''}
          </Button>
        </Popover.Target>
        <Popover.Dropdown>
          <Stack ref={outsideRef}>
            <Group justify="center">
              <NumberInput
                ref={ref}
                size={size}
                w={80}
                placeholder="From"
                value={inputValues[0]}
                hideControls={false}
                onChange={(e) => {
                  setInputValues([Number(e), inputValues[1]]);
                }}
                min={0}
              />
              <NumberInput
                size={size}
                w={80}
                placeholder="To"
                value={inputValues[1]}
                hideControls={false}
                onChange={(e) => {
                  setInputValues([inputValues[0], Number(e)]);
                }}
                min={0}
              />
            </Group>
            <Group justify="space-between" gap="xs" wrap="nowrap">
              <Button size={size} onClick={() => handleChange(false)} fullWidth>
                Apply
              </Button>
              <ActionIcon
                size={size}
                w={20}
                variant="outline"
                color="secondary"
                onClick={() => handleChange(true)}
                aria-label="Clear"
              >
                <RiCloseFill />
              </ActionIcon>
            </Group>
          </Stack>
        </Popover.Dropdown>
      </Popover>
    );
  }
);

export { NumberRangeInput };
